import { useDensity } from '@/theme/use-density';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { transportDestination } from '@/lib/transport/destination';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Home, Loader2, Scale } from 'lucide-react';
import { Link } from 'react-router';
import { getReservations } from '@/lib/booking/api';
import { Button } from '@/components/ui/button';
import { Hint } from '@/components/ui/hint';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { isRebookingConflictError } from '@/lib/rebooking/api';
import { offerExpiryOf } from '@/lib/rebooking/offer-expiry';
import { optionDeparted } from '@/lib/rebooking/flight-provider';
import { useRebookingAvailability } from '@/lib/rebooking/use-rebooking-availability';
import { isLockConflictError } from '@/lib/locks/types';
import type { RebookingConflict } from '@/lib/rebooking/types';
import {
  useAllocateCaseHotel,
  useCancelGroupSelection,
  useCareBoard,
  useCaseCareCards,
  useDisruptionGroupOptions,
  useGenerateGroupOptions,
  useIssueTicket,
  useSelectGroupOption,
} from '@/lib/disruptions/use-disruptions';
import type { DisruptionPnrGroupRow } from '@/lib/disruptions/types';
import { goingHomeOf, type IGoingHome } from '@/lib/disruptions/going-home';
import { routingDisruptionUrn, type PnrGroupScope } from '@/lib/pnr/scope';
import { useCaseNotifications, useForceAccept } from '@/lib/notifications/use-notifications';
import { useCaseOverview } from '@/routes/case-detail/use-case-overview';
import { useCaseTrips } from '@/lib/transport/use-trips';
import { splitTripsForGroup } from '@/lib/transport/trips';
import { useGroupVoucher } from '@/lib/transport/use-voucher';
import { voucherCoversTransport } from '@/lib/transport/voucher-coverage';
import { formatMinor } from '@/routes/disruptions/care-desk/care-desk-model';
import { cn } from '@/lib/cn';
import { joinCase, leaveCase } from '@/lib/ws/socket';
import { useGroupRecovery, useRecoveryAction } from '@/lib/recovery/use-recovery';
import { answeredFlight, hasRecovery, nexaBuys, providerName, purchasePhase, reacceptedCandidate } from '@/lib/recovery/recovery-model';
import { purchaseWhy } from '@/routes/disruptions/operar/views/cancelled/purchase-why';
import { impliesOvernight, journeyOf, recoveryDelay } from './pnr-status';
import { noOptionsReasonLabel } from './labels';
import {
  CancelSelectionDialog,
  ExcludeDialog,
  OverrideDialog,
  RejectOptionsDialog,
  type OverrideMode,
} from './pnr-action-dialogs';
import { JourneyRibbon } from '@/components/pnr/journey-ribbon';
import { FlightResolvePanel, policyScopeLabel } from '@/components/pnr/flight-resolve-panel';
import { PassengerAgesAction } from '@/components/pnr/passenger-ages-action';
import { LccPanel } from '@/components/lcc/lcc.panel';
import { LccStatusBar } from '@/components/lcc/lcc.status-bar';
import { lccApi } from '@/lib/lcc/api';
import { autoSearchDate } from '@/lib/lcc/auto-search';
import { HotelCard } from '@/components/pnr/hotel-card';
import { CompensationCardPanel } from '@/components/wallet/compensation-card-panel';
import { TransportCard } from '@/components/pnr/transport-card';
import { PassengersCard } from '@/components/pnr/passengers-card';
import { ActivityPanel } from '@/components/pnr/activity-panel';
import { PnrMoreActions } from '@/components/pnr/pnr-more-actions';
import { PaxAppLink } from '@/components/pnr/pax-app-link';
import { PnrUtilityActions } from '@/components/pnr/pnr-utility-actions';
import { usePnrUtilities } from '@/components/pnr/use-pnr-utilities';
import { useResendOffer } from '@/components/pnr/use-resend-offer';
import { OfferAcceptanceCard } from '@/components/pnr/offer-acceptance-card';
import { ForceAcceptDialog } from '@/components/pnr/force-accept-dialog';
import { ReaccommodationDiagram } from '@/components/pnr/reaccommodation-diagram';
import { FlightDetails } from '@/components/pnr/flight-details';
import { HotelWhyCard } from '@/components/pnr/hotel-why-card';
import { EntitlementCard } from '@/components/pnr/entitlement-card';
import {
  lastDecline,
  deriveNextUp,
  phoneOnFile,
  callbackRequested,
  deriveSteps,
  tripPhaseOf,
  type CardFacts,
  type PnrT,
  type StepState,
} from '@/components/pnr/pnr-steps';
import { deriveBand, type BandAction, type CardView, type StatusBand } from '@/components/pnr/pnr-band';
import { FieldLabel } from '@/components/pnr/airport-ui';
import { toneClasses } from '@/components/pnr/airport-tokens';
import { PnrCardHeader } from '@/components/pnr/pnr-card-header';
import { cardClock } from '@/components/pnr/card-time';
import { policyReasonText } from '@/lib/policy-reason/policy-reason';
import { PnrStatusBand } from '@/components/pnr/pnr-status-band';
import { PnrTripStrip, type TripCell } from '@/components/pnr/pnr-trip-strip';
import { CardAction, ServiceCard, ServiceIcon, ViewHeader } from '@/components/pnr/pnr-card-ui';
import { PnrPaxSummary } from '@/components/pnr/pnr-pax-summary';
import { PnrConversation } from '@/components/pnr/pnr-conversation';
import { AcceptanceClock } from '@/components/allocation/acceptance-clock';
import { useNow } from '@/components/allocation/use-now';
import {
  RecoveryFlightBody,
  ReplacementChooser,
  UnconfirmedPanel,
  type RecoveryRun,
} from '@/components/pnr/recovery-flight';

/** A failed write's HTTP status (a ky HTTPError, or a test double of one); else undefined. */
function httpStatus(err: unknown): number | undefined {
  return (err as { response?: { status?: number } })?.response?.status;
}

/** A failed write's 409 `code` (a ky HTTPError, or a test double of one); else undefined. */
async function conflictCode(err: unknown): Promise<string | undefined> {
  const response = (err as { response?: { status?: number; clone?: () => { json(): Promise<unknown> } } })?.response;
  if (response?.status !== 409) return undefined;
  try {
    const body = (await response.clone?.().json()) as { code?: unknown } | undefined;
    return typeof body?.code === 'string' ? body.code : undefined;
  } catch {
    return undefined;
  }
}

// THE PASSENGER CARD (PO 2026-10-31 redesign, accepted-offer-recovery P7) — the SAME
// dialog opened per PNR over the list, rebuilt to be read at a glance:
//   • HEADER — the PNR, who travels and where, «3 de 57 en Te necesitan», ↑ ↓ to the
//     previous / next PNR without closing, «Más acciones ▾» (the rare actions), close.
//   • STATUS BAND — ONE sentence of where the PNR is + ONE primary action (at most one
//     secondary). `deriveBand` never contradicts the state: a refused purchase is never
//     «el ticket se está emitiendo solo».
//   • THE TRIP IN ORDER — original flight → wait → hotel → new flight → arrival, one
//     vocabulary (Listo · En curso · Necesita decisión · No aplica · Pendiente).
//   • ONE CARD PER SERVICE (new flight · meals & card · hotel & transfer) with its detail
//     and its own actions; the passengers (masked) and the conversation beside them.
//   • DETAIL VIEWS — every capability of the old tabs (options, override, hotel picker,
//     card, transport, acceptance, activity…) one click away, «← Ficha» back.
// A failed purchase's recovery (§6.6) lives here as states of the card: needs a decision
// (offer the best, choose among the fresh options, search again, buy without asking,
// leave without flight), Nexa replacing, re-offered, unconfirmed (verify / give up).
// Data + mutations live in PnrDialogBody; the pieces are presentational (components/pnr/*);
// the derivations are pure (pnr-steps, pnr-band, lib/recovery). Realtime: the case WS
// room + the caller's live roster row — nothing polls.

/** Legacy refusals whose Operar copy carries the supplier's code (`DUFFEL_HTTP_422`). */
const REFUSED_BY_SUPPLIER: ReadonlySet<string> = new Set(['PURCHASE_FAILED', 'PURCHASE_REFUSED', 'PROVIDER_ERROR']);

/** ↑ ↓ through the list the card was opened from. */
export interface PnrCardNavigation {
  /** The list's PNRs in its order — the caller's live rows. */
  groups: readonly DisruptionPnrGroupRow[];
  /** The list's name («Te necesitan»); absent = the whole roster («42 de 104»). */
  listLabel?: string | undefined;
  /** Told the PNR ↑ / ↓ moved to (e.g. to keep a URL in step); the card moves either way. */
  onNavigate?: ((group: DisruptionPnrGroupRow) => void) | undefined;
}

interface PnrOperationCenterProps {
  /** The scope — disruption (control center) or case (case-detail). Scopes the
   *  per-group mutations' cache invalidation; the data reads are case-keyed at
   *  both scopes (the per-PNR options + reservations live on the case). */
  scope: PnrGroupScope;
  /** The opened PNR group (its member passengers ride nested under it). */
  group: DisruptionPnrGroupRow | null;
  onClose: () => void;
  /** The view it opens on — the card unless a caller names a service. */
  initialTab?: PnrTab | undefined;
  navigation?: PnrCardNavigation | undefined;
}

/** The views a caller may open the card on (Operar's «Elegir hotel» opens Hotel). */
export type PnrTab = Exclude<CardView, 'replacement'>;

/**
 * Back-compat alias for the disruption control center — wraps the scope-neutral
 * `PnrOperationCenter` with a disruption scope so existing call sites keep working.
 */
export function RosterPnrDrawer({
  disruptionUrn,
  group,
  onClose,
  initialTab,
  navigation,
}: {
  disruptionUrn: string;
  group: DisruptionPnrGroupRow | null;
  onClose: () => void;
  /** The view it opens on (Operar's «Elegir hotel» opens the Hotel view). */
  initialTab?: PnrTab | undefined;
  navigation?: PnrCardNavigation | undefined;
}) {
  return (
    <PnrOperationCenter
      scope={{ kind: 'disruption', disruptionUrn }}
      group={group}
      onClose={onClose}
      initialTab={initialTab}
      navigation={navigation}
    />
  );
}

export function PnrOperationCenter({ scope, group, onClose, initialTab, navigation }: PnrOperationCenterProps) {
  const open = !!group;
  const [maximized, setMaximized] = useState(false);
  const density = useDensity();
  // ↑ ↓ move the card along the caller's list; a new PNR from the caller resets it.
  const [current, setCurrent] = useState<string | undefined>(group?.subCaseUrn);
  useEffect(() => setCurrent(group?.subCaseUrn), [group?.subCaseUrn]);
  const list = navigation?.groups ?? [];
  const shown = (current ? list.find((g) => g.subCaseUrn === current) : undefined) ?? group;
  const index = shown ? list.findIndex((g) => g.subCaseUrn === shown.subCaseUrn) : -1;
  const go = (to: DisruptionPnrGroupRow | undefined): (() => void) | undefined =>
    to
      ? () => {
          setCurrent(to.subCaseUrn);
          navigation?.onNavigate?.(to);
        }
      : undefined;

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? undefined : onClose())}>
      <DialogContent
        className={cn(
          'pnr-workspace flex flex-col gap-0 overflow-hidden p-0',
          // The dialog's own close sits in the header row, sized like its neighbours.
          '[&>button:last-child]:right-4 [&>button:last-child]:top-3.5 [&>button:last-child]:h-10 [&>button:last-child]:w-10 [&>button:last-child]:rounded-lg [&>button:last-child]:border [&>button:last-child]:border-input [&>button:last-child]:opacity-100 sm:[&>button:last-child]:right-6',
          maximized
            ? 'inset-0 h-auto w-auto max-w-none translate-x-0 translate-y-0 rounded-none border-0'
            : 'h-[92dvh] w-[96vw] max-w-6xl rounded-2xl',
        )}
        data-density={density.compact ? 'compact' : 'comfortable'}
        data-maximized={maximized}
        aria-describedby={undefined}
        data-testid="pnr-workspace"
      >
        <DialogTitle className="sr-only">{shown?.pnrLabel}</DialogTitle>
        {shown ? (
          <PnrDialogBody
            key={shown.subCaseUrn}
            scope={scope}
            group={shown}
            onClose={onClose}
            initialTab={shown.subCaseUrn === group?.subCaseUrn ? initialTab : undefined}
            position={
              index >= 0 ? { index: index + 1, total: list.length, listLabel: navigation?.listLabel } : undefined
            }
            onPrev={index > 0 ? go(list[index - 1]) : undefined}
            onNext={index >= 0 && index < list.length - 1 ? go(list[index + 1]) : undefined}
            maximized={maximized}
            onToggleMaximize={() => setMaximized((m) => !m)}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

/** One view of the card. Hidden by class (not unmounted) so switching is instant (TDS §4.2);
 *  a `lazy` view (one the operator rarely opens) mounts on its first visit, then stays. */
function CardViewPane({
  value,
  active,
  lazy = false,
  children,
}: {
  value: CardView;
  active: CardView;
  lazy?: boolean;
  children: ReactNode;
}) {
  const on = value === active;
  const [visited, setVisited] = useState(on);
  useEffect(() => {
    if (on) setVisited(true);
  }, [on]);
  return (
    <div
      className={cn('flex min-h-0 flex-1 flex-col', !on && 'hidden')}
      data-state={on ? 'active' : 'inactive'}
      data-testid={`pnr-view-${value}`}
    >
      {!lazy || visited || on ? children : null}
    </div>
  );
}

/** A service card's assigned / delivered lines (what was decided, what they hold). */
function StepLines({ step }: { step: StepState }) {
  // An empty value («—») is not drawn; its explanation (e.g. «no card handed over») is.
  const lines = [step.assigned, step.delivered].filter((l) => (l.primary && l.primary !== '—') || l.secondary);
  return (
    <div className="flex flex-col gap-1">
      {lines.map((l, idx) => (
        <div key={idx} className="flex flex-col">
          {l.primary && l.primary !== '—' ? (
            <span className="text-[15px] [overflow-wrap:anywhere]">{l.primary}</span>
          ) : null}
          {l.secondary ? <span className="text-sm text-muted-foreground [overflow-wrap:anywhere]">{l.secondary}</span> : null}
          {l.acceptanceExpiresAt !== undefined ? <HoldClock expiresAt={l.acceptanceExpiresAt} /> : null}
        </div>
      ))}
    </div>
  );
}

/** The held hotel's commercial acceptance clock, ticking on the card. */
function HoldClock({ expiresAt }: { expiresAt: string }) {
  const now = useNow(!!expiresAt && Number.isFinite(Date.parse(expiresAt)));
  return (
    <span className="mt-1" data-testid="pnr-hotel-acceptance-clock">
      <AcceptanceClock expiresAt={expiresAt || undefined} now={now} />
    </span>
  );
}

function PnrDialogBody({
  scope,
  group,
  onClose,
  initialTab,
  position,
  onPrev,
  onNext,
  maximized,
  onToggleMaximize,
}: {
  scope: PnrGroupScope;
  group: DisruptionPnrGroupRow;
  onClose: () => void;
  initialTab?: PnrTab | undefined;
  position?: { index: number; total: number; listLabel?: string | undefined } | undefined;
  onPrev?: (() => void) | undefined;
  onNext?: (() => void) | undefined;
  maximized: boolean;
  onToggleMaximize: () => void;
}) {
  const { t: tRoster } = useTranslation('roster');
  const { t, i18n } = useTranslation('pnr');
  const { t: tLcc } = useTranslation('lcc');
  // The card's dates in the console's language and the station's time, 24 h («31 oct · 23:13»).
  const clock = cardClock(i18n.resolvedLanguage ?? i18n.language, group.origin);
  const { t: tActions } = useTranslation('pnrActions');
  const lccProviders = useQuery({ queryKey: ['lcc', 'providers'], queryFn: lccApi.providers, retry: false });
  const externalPurchaseOnly = lccProviders.data?.externalPurchaseOnly === true;
  const legacyActionsAvailable = lccProviders.isSuccess && !externalPurchaseOnly;
  // #771 — THIS group's external (LCC) purchase journal, read at drawer level
  // (the same query LccStatusBar/LccPanel make; react-query dedupes) so the
  // band/steps reflect a CONFIRMED replacement purchase instead of pushing
  // "Generate options" on a group already resolved at the vendor.
  const lccPurchases = useQuery({
    queryKey: ['lcc', 'purchases', group.caseUrn, group.subCaseUrn],
    queryFn: () => lccApi.purchases(group.caseUrn, group.subCaseUrn),
    retry: false,
  });
  const lccAttempted = useQuery<boolean>({
    queryKey: ['lcc', 'attempted', group.caseUrn, group.subCaseUrn],
    queryFn: async () => false,
    enabled: false,
    initialData: false,
  });
  const externalBooking = lccPurchases.data?.purchases.find((p) => p.status === 'CONFIRMED');
  // The pure derivations speak in KEYS; hand them this namespace's translator.
  const tr: PnrT = (key, opts) => String(t(key, opts ?? {}));
  const select = useSelectGroupOption(scope);
  const ticket = useIssueTicket(scope);
  const generate = useGenerateGroupOptions(scope);
  const cancelSelection = useCancelGroupSelection(scope);
  const resendOffer = useResendOffer(group);
  const allocate = useAllocateCaseHotel(scope);
  const force = useForceAccept(group.caseUrn);
  const recoveryQuery = useGroupRecovery(group);
  const recoveryAction = useRecoveryAction(scope, group);
  const [conflict, setConflict] = useState<RebookingConflict | null>(null);
  const [lockedByName, setLockedByName] = useState<string | null>(null);
  const [actionError, setActionError] = useState<{ title: string; description: string } | null>(null);
  const [overrideMode, setOverrideMode] = useState<OverrideMode | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [excludeOpen, setExcludeOpen] = useState(false);
  const [view, setView] = useState<CardView>(initialTab ?? 'overview');
  // Set once the operator generates options for THIS PNR in-session: the group row's
  // `optionsCount` is denormalised from cases ASYNC (rebooking → bus → cases), so it's
  // still 0 right after generation — without this the options query would stay gated
  // off and the freshly-generated options would never load into the open drawer.
  const [justGenerated, setJustGenerated] = useState(false);

  // Drill the per-group options when the group reports it has any (from bulk S2/S6)
  // OR the operator just generated them here OR the group is in a terminal no-options
  // state (so we fetch the rebooking view's noOptionsReason — the roster row carries
  // it for the bulk path but NOT the direct per-PNR generate path); otherwise render
  // the empty state with no fetch.
  const terminalNoOptions =
    group.rebookingStatus === 'NO_OPTIONS' || group.rebookingStatus === 'NOT_APPLICABLE';
  const optionsQuery = useDisruptionGroupOptions(
    group.caseUrn,
    group.subCaseUrn,
    group.optionsCount > 0 || justGenerated || terminalNoOptions,
  );
  const options = optionsQuery.data?.options ?? [];
  const selectedUrn = optionsQuery.data?.selectedOptionUrn ?? group.selectedOptionUrn;
  const liveStatus = optionsQuery.data?.status ?? group.rebookingStatus;
  const ticketNumber = optionsQuery.data?.ticketNumber ?? group.ticketNumber;
  // Prefer the LIVE rebooking view's reason over the roster row: a per-PNR (direct)
  // generate persists the reason on the rebooking group-state + view but does NOT
  // emit the roster-updating event, so group.noOptionsReason stays stale (POL-13).
  const liveNoOptionsReason = optionsQuery.data?.noOptionsReason ?? group.noOptionsReason;
  const isTicketed = !!ticketNumber;
  const isSelected = !!selectedUrn || liveStatus === 'SELECTED';
  // Pre-run policy gate (rebooking TDS §18): resolved against THIS PNR's disrupted
  // leg, so the panel can withhold Generate up front when the station's policy
  // disables the engine. Cached per station/leg across the roster's PNRs.
  const policyGate = useRebookingAvailability({
    airport: group.origin,
    leg: group.origin && group.destination ? `${group.origin}-${group.destination}` : undefined,
  });
  const selectedOption = selectedUrn ? options.find((o) => o.urn === selectedUrn) : undefined;
  const selectedSegments = selectedOption?.segments;
  // The offer expired (PO 2026-11-03): its flight left with no ticket, or every option did.
  // The live read wins over the row; the row speaks until it is read.
  const expired = isTicketed
    ? null
    : offerExpiryOf(group, Date.now(), {
        ...(selectedOption ? { selected: { provider: selectedOption.provider, segments: selectedOption.segments } } : {}),
        ...(optionsQuery.data && options.length > 0 && !selectedUrn
          ? { optionsExpired: options.every((o) => o.state === 'EXPIRED') }
          : {}),
      });
  // Onward-connection feasibility for the SELECTED option (protect-to-final).
  const selectedConnection = selectedOption?.protectToFinal
    ? {
        onwardFlightNumber: selectedOption.onwardFlightNumber,
        onwardOrigin: selectedOption.onwardOrigin,
        onwardDepartureUtc: selectedOption.onwardDepartureUtc,
        layoverMinutes: selectedOption.layoverMinutes,
        connectionFeasible: selectedOption.connectionFeasible,
        overnightAtHub: selectedOption.overnightAtHub,
      }
    : null;

  // Care-only (GOAL 1): the case-level `rebookingDisabled` flag rides the case overview;
  // react-query dedupes with case-detail's own read. Presentation only — every action
  // stays available and the wire is untouched.
  const overview = useCaseOverview(group.caseUrn);
  const careOnly = overview.data?.rebookingDisabled === true;
  // Stay-home (GOAL 2): operator-declared "stays at own home" on the sub-case.
  const stayingHome = group.passengerElection === 'LOCAL_RESIDENT';
  // #811: the passenger's own "prefiero ir a mi domicilio" (also elects stay-home).
  const goingHome = goingHomeOf(group);

  // Join this PNR's CASE room while the card is open so the per-case `CaseChanged`
  // WS events (incl. `kind:'booking'` from the reservation settlement) reach this
  // view and `applyEvent` invalidates `['cases', caseUrn, 'reservations']` live.
  useEffect(() => {
    joinCase(group.caseUrn);
    return () => leaveCase(group.caseUrn);
  }, [group.caseUrn]);

  // The WS-invalidated reservations slice for this case — matched by SUB-CASE urn first,
  // the denorm `hotelReservationUrn` as fallback; terminal rows never read as "reserved".
  const reservationsQuery = useQuery({
    queryKey: ['cases', group.caseUrn, 'reservations'],
    queryFn: () => getReservations(group.caseUrn),
    enabled: !!group,
  });
  const reservation = reservationsQuery.data?.find(
    (r) =>
      (r.subCaseUrn === group.subCaseUrn ||
        (!!group.hotelReservationUrn && r.reservationUrn === group.hotelReservationUrn)) &&
      r.status !== 'CANCELLED' &&
      r.status !== 'FAILED' &&
      r.status !== 'CANCEL_FAILED',
  );
  const utilities = usePnrUtilities(group, !!reservation);

  // Provisional overnight need from the chosen flight's departure vs the disrupted day
  // (mirrors backend deriveHotelRequirement) until the `requiresHotel` denorm reconciles.
  const assignedDepartureIso = selectedSegments?.[0]?.departure ?? group.assignedFlight?.departureAt;
  const provisionalOvernight =
    isSelected && group.requiresHotel === undefined
      ? impliesOvernight(group.departureAt, assignedDepartureIso)
      : undefined;
  // The hotel verdict, exactly as HotelCard reads it — the card and the view must agree.
  const overnightAtHub = selectedConnection?.overnightAtHub;
  const connectionUnvalidated = !!selectedConnection && selectedConnection.connectionFeasible === undefined;
  const hotelNeeded = group.requiresHotel === true || provisionalOvernight === true || overnightAtHub === true;
  const hotelSameDay =
    !overnightAtHub &&
    !connectionUnvalidated &&
    (group.requiresHotel === false || provisionalOvernight === false);

  const recovery = recoveryDelay(group.departureAt, assignedDepartureIso);
  // Where Nexa's purchase of the chosen flight is (Duffel / Atlas). A ticket outranks it.
  const purchaseRaw = purchasePhase(group.nexaPurchase);
  const purchase = isTicketed ? null : purchaseRaw;

  // ── The SAME queries the service views make (react-query dedupes), read here so the
  // card can say what each service holds.
  const routingUrn = routingDisruptionUrn(scope, group);
  const notifications = useCaseNotifications(group.caseUrn);
  const notification = notifications.rowFor(group.subCaseUrn);
  const tripsQuery = useCaseTrips(group.caseUrn);
  const myTrips = useMemo(
    () =>
      splitTripsForGroup(tripsQuery.data, {
        subCaseUrn: group.subCaseUrn,
        pnrLabel: group.pnrLabel,
        pnrUrn: group.pnrUrn,
        paxUrns: group.passengers.map((p) => p.paxUrn),
      }).mine,
    [tripsQuery.data, group.subCaseUrn, group.pnrLabel, group.pnrUrn, group.passengers],
  );
  // The group's Uber voucher (same cache entry the transport section reads).
  const groupVoucher = useGroupVoucher(group.caseUrn, group.subCaseUrn);
  // Where a ride would go — a CONFIRMED hotel or the stay-home.
  const destination = transportDestination(group, reservation);
  const transportArranged = myTrips.length > 0 || !!group.transfer || voucherCoversTransport(groupVoucher.data);
  const careBoard = useCareBoard(routingUrn);
  const caseCards = useCaseCareCards(routingUrn ? undefined : group.caseUrn);
  const boardCard = careBoard.data?.cards.find((c) => c.subCaseUrn === group.subCaseUrn);
  const caseCard = caseCards.data?.find((c) => c.subCaseUrn === group.subCaseUrn);
  const cardFacts: CardFacts | undefined = boardCard
    ? {
        status: boardCard.status,
        last4: boardCard.last4,
        loadedMinor: boardCard.faceValueMinor + boardCard.toppedUpMinor,
        availableMinor: boardCard.availableMinor,
        currency: boardCard.currency,
        balancesKnown: careBoard.data?.known.wallet !== false,
      }
    : caseCard
      ? {
          status: caseCard.status,
          last4: caseCard.last4,
          loadedMinor: caseCard.faceValueMinor + (caseCard.toppedUpMinor ?? 0),
          availableMinor: caseCard.availableMinor,
          currency: caseCard.currency,
          balancesKnown: true,
        }
      : undefined;

  // Assigned-flight facts — live option first, the async denorm as fallback.
  const hasAssigned = isSelected || isTicketed;
  const journey = selectedSegments && selectedSegments.length > 1 ? journeyOf(selectedSegments) : null;
  const assignedRoute = hasAssigned
    ? journey
      ? [journey.from, ...journey.via, journey.to].join(' → ')
      : [group.assignedFlight?.origin ?? group.origin, group.assignedFlight?.destination ?? group.destination]
          .filter(Boolean)
          .join(' → ')
    : undefined;
  const assignedFlightNumber = hasAssigned
    ? (group.assignedFlight?.number ?? selectedSegments?.[0]?.flightNumber)
    : undefined;
  const assignedWhen =
    hasAssigned && assignedDepartureIso
      ? clock.when(assignedDepartureIso, selectedSegments?.[0]?.origin ?? group.assignedFlight?.origin)
      : undefined;
  const hotelPart = group.offer?.parts?.hotel;
  const nights =
    hotelPart?.checkInUtc && hotelPart.checkOutUtc
      ? Math.max(1, Math.round((Date.parse(hotelPart.checkOutUtc) - Date.parse(hotelPart.checkInUtc)) / 86_400_000))
      : undefined;
  const noOptionsCopy = noOptionsReasonLabel(liveNoOptionsReason);

  const steps = deriveSteps(
    {
      group,
      liveStatus,
      isSelected,
      isTicketed,
      ticketNumber,
      optionsCount: options.length,
      optionsExpired: expired?.kind === 'options',
      noOptionsCopy,
      assignedFlightNumber,
      assignedRoute,
      assignedWhen,
      disruptedWhen: group.departureAt ? clock.when(group.departureAt) : undefined,
      recovery,
      hotel: {
        reservation,
        needs: hotelNeeded,
        sameDay: hotelSameDay,
        stay: Number.isFinite(nights) && nights ? tr('steps.hotel.nights', { count: nights }) : undefined,
        stayingHome,
      },
      card: cardFacts,
      formatMoney: formatMinor,
      trips: { mine: myTrips, loaded: !tripsQuery.isLoading },
      voucher: groupVoucher.data,
      notification,
      formatWhen: (iso) => (iso ? clock.when(iso) : '—'),
      careOnly,
      externalBooking,
      purchase,
    },
    tr,
  );
  const stepOf = (key: StepState['key']): StepState | undefined => steps.find((s) => s.key === key);
  const flightStep = stepOf('flight');
  const hotelStep = stepOf('hotel')!;
  const mealsStep = stepOf('meals')!;
  const transportStep = stepOf('transport')!;
  // Care-only with no flight of ours and none from the manifest: no flight service.
  const showFlight = !!flightStep;
  const activeView: CardView = view === 'flight' && !showFlight ? 'overview' : view;
  const manifestFlight = careOnly && !hasAssigned && !externalBooking ? group.manifestNextFlight : undefined;
  // Never recommend a flight that already left (PO 2026-11-03: «Seleccionar #1 · LA0287» over
  // options that had all departed).
  const liveOptions = options.filter((o) => o.state !== 'EXPIRED' && !optionDeparted(o));
  const recommended = liveOptions.find((o) => o.rank === 1) ?? liveOptions[0];
  const awaiting = notification?.acceptanceState === 'awaiting';
  const nextUp = deriveNextUp(
    {
      liveStatus,
      isSelected,
      isTicketed,
      optionsCount: options.length,
      noOptionsCopy,
      recommendedFlight: recommended?.segments[0]?.flightNumber,
      hotelNeeded,
      hotelReserved: !!reservation,
      cardIssued: !!cardFacts && cardFacts.status !== 'FAILED' && cardFacts.status !== 'FAILED_COMPENSATED',
      cardEntitled: !!group.mealVoucher && !group.careOutcome,
      transportArranged,
      transportSettled: group.transportOutcome?.outcome === 'OWN_MEANS',
      acceptance: notification?.acceptanceState,
      // A response proves the offer arrived; only an unanswered failure is one.
      deliveryFailed:
        notification?.deliveryState === 'failed' &&
        notification.acceptanceState !== 'accepted' &&
        notification.acceptanceState !== 'declined',
      declineIntent: lastDecline(group)?.intent,
      hotelDeclined: lastDecline(group)?.hotel,
      careOnly,
      externalPurchased: !!externalBooking,
      purchase,
      nexaBuys: nexaBuys(group),
    },
    tr,
  );

  // Clocks at the right airport: departures at the origin, arrivals at the destination.
  const finalDest =
    group.itinerary?.finalDestination ??
    group.connection?.finalDestination ??
    group.itinerary?.legs[group.itinerary.legs.length - 1]?.destination ??
    group.destination;
  // A moment at the station: the time today, the date too on another day.
  const depClock = (iso: string): string => clock.moment(iso);
  const why = purchaseWhy(group.nexaPurchase);
  // A legacy refusal in Operar's words — except the refusals worded with the supplier's code:
  // the card names the supplier, never a vendor / HTTP code.
  // The refusal in words whenever the row carries one (a refused purchase, or the re-accepted
  // flight's purchase inside a recovery).
  const refusalWhy = !group.nexaPurchase?.reason
    ? undefined
    : REFUSED_BY_SUPPLIER.has(why.key)
        ? tr('recovery.review.refused', { provider: providerName(group.nexaPurchase?.provider ?? group.selectedProvider) })
        : String(
            t(`operateCancel:need.why.purchase.${why.key}`, {
              fields: why.fields.map((f) => t(`operateCancel:need.missing.${f}`)).join(', '),
              count: why.pax,
              price: why.price ?? '—',
              cap: why.cap ?? '—',
            }),
          );
  const reviewWhy = purchase === 'REVIEW' ? refusalWhy : undefined;
  const reaccepted = reacceptedCandidate(recoveryQuery.data, group.lastOfferResponse);
  const answerFlight = answeredFlight(recoveryQuery.data, group.lastOfferResponse, assignedFlightNumber ?? group.assignedFlight?.number);
  // Nexa's purchase failed AFTER their yes: the conversation says so (the ticket did not come).
  const purchaseFailed = (() => {
    const p = group.nexaPurchase;
    const answer = group.lastOfferResponse;
    if (!p || answer?.response !== 'ACCEPTED') return undefined;
    if (p.status !== 'NEEDS_REVIEW' && p.recovery?.state !== 'NEEDS_OPERATOR') return undefined;
    if (!(Date.parse(p.updatedAt) >= Date.parse(answer.at))) return undefined;
    return { at: p.updatedAt, why: refusalWhy };
  })();

  const ticketedFlight =
    isTicketed || purchaseRaw === 'RECOVERED'
      ? { flight: assignedFlightNumber ?? group.assignedFlight?.number, when: assignedWhen }
      : undefined;
  const bandInput = {
    nextUp,
    recovery: purchaseRaw
      ? {
          phase: purchaseRaw,
          view: recoveryQuery.data,
          provider: group.nexaPurchase?.provider ?? group.selectedProvider,
          reason: group.nexaPurchase?.recovery?.reason,
          acceptBy: group.nexaPurchase?.recovery?.acceptBy,
          rowCandidates: group.nexaPurchase?.recovery?.candidates,
          viewLoading: recoveryQuery.isLoading,
          viewFailed: recoveryQuery.isError,
          reviewWhy,
          reviewReason: group.nexaPurchase?.reason,
          acceptedFlight: recoveryQuery.data?.accepted?.flightNumber ?? assignedFlightNumber,
          reaccepted,
          refusalWhy,
        }
      : undefined,
    formatTime: depClock,
    ticketed: ticketedFlight,
    legacyActions: legacyActionsAvailable,
    hotelAllocatable: !!routingUrn,
    canResend: !!notification && notification.acceptanceState !== 'none',
    awaiting,
    canCall: phoneOnFile(group),
    // An answered «llámenme» — unless a newer offer already awaits the passenger.
    callback: callbackRequested(group) && !awaiting,
    expired,
  };
  const band: StatusBand =
    externalPurchaseOnly && !externalBooking && !isTicketed && !purchase
      ? externalSupervisedBand()
      : deriveBand(bandInput, tr);

  /** The external-only deployment: the LCC journal's last purchase is the band. */
  function externalSupervisedBand(): StatusBand {
    const receipt =
      lccPurchases.data?.purchases.find((p) => p.status === 'CONFIRMED') ?? lccPurchases.data?.purchases[0];
    const base: StatusBand = {
      kind: 'external-supervised',
      tone: 'info',
      kicker: tr('band.kicker.supervised'),
      title: tLcc('supervisedTitle'),
      body: tLcc('supervisedNext'),
      primary: { kind: 'view', view: 'flight', label: tr('band.action.openFlight') },
    };
    if (receipt) {
      return {
        ...base,
        kind: `external-${receipt.status.toLowerCase()}`,
        tone: receipt.status === 'FAILED' ? 'alert' : 'warn',
        title: tLcc('receiptStatus', { status: tLcc(`status.${receipt.status}`) }),
        body: tLcc('receiptCheckExisting'),
      };
    }
    if (lccAttempted.data) {
      return { ...base, kind: 'external-unknown', tone: 'warn', title: tLcc('receiptStatus', { status: tLcc('status.UNKNOWN') }), body: tLcc('receiptCheckExisting') };
    }
    if (lccPurchases.isError) return { ...base, kind: 'external-unavailable', tone: 'warn', body: tLcc('receiptUnavailable') };
    if (lccPurchases.isPending) return { ...base, kind: 'external-loading', body: tLcc('receiptLoading') };
    return base;
  }

  // Journey ribbon derivations. The manifest's legs sometimes carry a BARE number
  // ("116") where the roster row carries the full designator ("LA116") — the fuller
  // label wins on the matching leg.
  const legs = (
    group.itinerary?.legs && group.itinerary.legs.length > 0
      ? group.itinerary.legs
      : [{ flightNumber: group.flightNumber, origin: group.origin, destination: group.destination }]
  ).map((leg) =>
    leg.flightNumber &&
    group.flightNumber &&
    /^\d+$/.test(leg.flightNumber) &&
    group.flightNumber.endsWith(leg.flightNumber)
      ? { ...leg, flightNumber: group.flightNumber }
      : leg,
  );
  const isConnection = group.itinerary?.isConnection || !!group.connection || legs.length > 1;
  const disruptedIdx = legs.findIndex((l) => l.origin === group.origin && l.destination === group.destination);

  const busy = select.isPending;
  // A re-accommodation is committed (chosen or ticketed).
  const resolved = isSelected || isTicketed;

  const runSelect = async (optionUrn: string): Promise<void> => {
    if (!legacyActionsAvailable) return;
    setConflict(null);
    setLockedByName(null);
    setActionError(null);
    try {
      // BUG-RUN6-009: pass pax count so the seat hold reserves seats for ALL passengers
      await select.mutateAsync({
        subCaseUrn: group.subCaseUrn,
        caseUrn: group.caseUrn,
        optionUrn,
        ...(group.paxCount && group.paxCount > 0 ? { seats: group.paxCount } : {}),
      });
    } catch (e) {
      if (isLockConflictError(e)) setLockedByName(e.holderName);
      else if (isRebookingConflictError(e)) setConflict(e.conflict);
      else
        setActionError({
          title: tRoster('drawer.selectError.title'),
          // 409 OPTION_DEPARTED: a stale option whose flight already left (PO 2026-11-03).
          description: (await conflictCode(e)) === 'OPTION_DEPARTED'
            ? tRoster('drawer.selectError.departed')
            : tRoster('drawer.selectError.description'),
        });
    }
  };

  const runGenerate = async (forceRun?: { reason: string }): Promise<void> => {
    if (!legacyActionsAvailable) return;
    setConflict(null);
    setLockedByName(null);
    setActionError(null);
    try {
      await generate.mutateAsync({
        subCaseUrn: group.subCaseUrn,
        caseUrn: group.caseUrn,
        ...(forceRun ? { force: forceRun } : {}),
      });
      setJustGenerated(true);
    } catch (e) {
      if (isLockConflictError(e)) setLockedByName(e.holderName);
      // ALREADY_TICKETED (the ticket landed while this card was open) surfaces as the
      // same conflict banner select uses — not a silent no-op.
      else if (isRebookingConflictError(e)) setConflict(e.conflict);
      else
        setActionError({
          title: tRoster('drawer.generateError.title'),
          description: tRoster('drawer.generateError.description'),
        });
    }
  };

  // The offer expired — its flight, or every option, left (PO 2026-11-03: «nunca un callejón
  // sin salida»): a new search. They had accepted it → the recovery's search against what they
  // accepted (the card becomes the recovery chooser, and an offer to the passenger goes through
  // it); not accepted → the selection is released (already released by rebooking's sweep is
  // fine) and the PNR searched again.
  const searchFreshPending = cancelSelection.isPending || generate.isPending || recoveryAction.isPending;
  const runSearchFresh = async (): Promise<void> => {
    if (!expired) return;
    setConflict(null);
    setLockedByName(null);
    setActionError(null);
    const searchError = {
      title: tRoster('drawer.expired.searchError.title'),
      description: tRoster('drawer.expired.searchError.description'),
    };
    if (expired.kind === 'selected' && expired.accepted) {
      recoveryAction.mutate({ kind: 'search' }, { onError: () => setActionError(searchError) });
      return;
    }
    if (expired.kind === 'selected') {
      try {
        await cancelSelection.mutateAsync({
          subCaseUrn: group.subCaseUrn,
          caseUrn: group.caseUrn,
          reason: tRoster('drawer.expired.cancelReason', { flight: expired.flight ?? '—' }),
        });
      } catch (e) {
        if (isLockConflictError(e)) return setLockedByName(e.holderName);
        if (isRebookingConflictError(e)) return setConflict(e.conflict);
        // 404: nothing selected any more — rebooking's sweep released it first. Search anyway.
        if (httpStatus(e) !== 404) return setActionError(searchError);
      }
    }
    await runGenerate();
  };

  const runIssueTicket = async (): Promise<void> => {
    if (!legacyActionsAvailable) return;
    setLockedByName(null);
    try {
      await ticket.mutateAsync({ subCaseUrn: group.subCaseUrn, caseUrn: group.caseUrn });
    } catch (e) {
      if (isLockConflictError(e)) setLockedByName(e.holderName);
    }
  };

  // Per-PNR force-accept — the SAME supervisor-gated, audited SEAM-1 write; the dialog
  // collects the reason and shows the server's refusal inline.
  const doForce = (reason: string): void => {
    if (!notification) return;
    force.mutate({
      subCaseUrn: group.subCaseUrn,
      body: { offerHash: notification.offerHash ?? '', ...(reason ? { reason } : {}) },
    });
  };
  const forceError = force.isError
    ? force.error instanceof Error
      ? force.error.message
      : t('notification.requestFailed')
    : null;

  const recoveryRun: RecoveryRun = {
    run: (action) => recoveryAction.mutate(action),
    pending: recoveryAction.isPending,
    error: recoveryAction.error,
    action: recoveryAction.variables?.kind,
  };
  const recoveryProps = {
    group,
    view: recoveryQuery.data,
    loading: recoveryQuery.isLoading,
    failed: recoveryQuery.isError,
    onRetry: () => void recoveryQuery.refetch(),
    recovery: recoveryRun,
    refusalWhy,
  };

  // #743: the acceptance gate (N6) requires passenger acceptance or an explicit operator
  // override before the irreversible ticket ISSUE.
  const ticketBlockedByAcceptance = awaiting;
  const spinner = <Loader2 className="h-4 w-4 animate-spin" aria-hidden />;

  /** The band's actions, wired to this card's mutations. */
  function bandButton(a: BandAction, emphasis: 'primary' | 'secondary'): ReactNode {
    const big = emphasis === 'primary';
    if (a.kind === 'forceAccept') {
      if (!notification) return null;
      return (
        <ForceAcceptDialog
          key={`force-${notification.offerHash ?? 'missing-offer'}`}
          groupLabel={group.pnrLabel}
          pending={force.isPending}
          offerHashMissing={!notification.offerHash}
          error={forceError}
          onConfirm={doForce}
        />
      );
    }
    let onClick: () => void = () => undefined;
    let pending = false;
    let disabled = false;
    let title: string | undefined;
    switch (a.kind) {
      case 'select':
        onClick = () => recommended && void runSelect(recommended.urn);
        pending = busy;
        disabled = !recommended;
        break;
      case 'generate':
        onClick = () => void runGenerate();
        pending = generate.isPending;
        break;
      case 'ticket':
        onClick = () => void runIssueTicket();
        pending = ticket.isPending;
        disabled = ticketBlockedByAcceptance;
        title = ticketBlockedByAcceptance ? t('nextUp.ticket.awaitingTitle') : undefined;
        break;
      case 'hotel':
        onClick = () =>
          routingUrn &&
          allocate.mutate({ caseUrn: group.caseUrn, subCaseUrn: group.subCaseUrn, disruptionUrn: routingUrn });
        pending = allocate.isPending;
        break;
      case 'resend':
        onClick = resendOffer.run;
        pending = resendOffer.isPending;
        break;
      case 'resendTicket':
        onClick = () => utilities.runResend();
        pending = utilities.resendPending;
        break;
      case 'call':
        onClick = utilities.runCall;
        pending = utilities.resendPending;
        break;
      case 'contact':
        onClick = utilities.openContact;
        break;
      case 'view':
        onClick = () => setView(a.view ?? 'overview');
        break;
      case 'recoverySearch':
        onClick = () => recoveryAction.mutate({ kind: 'search' });
        pending = recoveryAction.isPending;
        break;
      case 'recoveryOffer':
        onClick = () => a.optionUrn && recoveryAction.mutate({ kind: 'offer', optionUrn: a.optionUrn });
        pending = recoveryAction.isPending;
        break;
      case 'recoveryReconcile':
        onClick = () => recoveryAction.mutate({ kind: 'reconcile' });
        pending = recoveryAction.isPending;
        break;
      case 'searchFresh':
        onClick = () => void runSearchFresh();
        pending = searchFreshPending;
        break;
    }
    return (
      <Button
        key={`${emphasis}-${a.kind}`}
        type="button"
        variant={big && band.tone !== 'ok' ? 'default' : 'outline'}
        className={big ? 'h-11 px-4 text-[15px] font-bold' : 'h-9 px-3 text-[13px]'}
        onClick={onClick}
        disabled={disabled || pending}
        {...(title ? { title } : {})}
        data-testid={`pnr-${emphasis}-${a.kind}`}
      >
        {pending ? spinner : null}
        {a.label}
      </Button>
    );
  }

  // PO 2026-10-28: the airline has no flight for this PNR → Nexa searches other airlines
  // by itself (Duffel / Atlas) and shows the offers as a proposal a supervisor approves.
  const noOwnFlight =
    liveStatus === 'NO_OPTIONS' &&
    options.length === 0 &&
    !optionsQuery.isLoading &&
    legacyActionsAvailable &&
    !externalBooking &&
    !careOnly;

  // ── The service views (the old tabs) — built once.
  const optionsPanel = (
    <FlightResolvePanel
      externalPurchaseOnly={externalPurchaseOnly}
      externalPurchased={!!externalBooking}
      legacyActionsAvailable={legacyActionsAvailable}
      externalCandidates={
        <LccPanel
          key={`${externalPurchaseOnly ? 'external-only' : 'mixed'}:${noOwnFlight ? 'auto' : 'manual'}`}
          initiallyExpanded={externalPurchaseOnly || !!externalBooking || noOwnFlight}
          {...(noOwnFlight ? { autoSearch: { departureDate: autoSearchDate(group.departureAt, group.origin) } } : {})}
          caseUrn={group.caseUrn}
          groupId={group.subCaseUrn}
          origin={group.origin}
          destination={group.connection?.finalDestination ?? group.destination}
          passengerIds={group.passengers.map((p) => p.paxUrn)}
        />
      }
      options={options}
      selectedUrn={selectedUrn}
      isSelected={isSelected}
      isTicketed={isTicketed}
      busy={busy}
      generating={generate.isPending}
      hasGenerated={generate.isSuccess}
      optionsLoading={optionsQuery.isLoading}
      optionsError={optionsQuery.isError}
      liveStatus={liveStatus}
      noOptionsReason={liveNoOptionsReason}
      governingPolicyUrn={group.governingPolicyUrn}
      providerAttempts={optionsQuery.data?.providerAttempts ?? group.flightProviderAttempts}
      policyGate={policyGate}
      careOnly={careOnly}
      agesAction={
        <PassengerAgesAction scope={scope} group={group} entered={optionsQuery.data?.passengerAges} testId="drawer-ages" />
      }
      onGenerate={() => void runGenerate()}
      onForceGenerate={(reason) => void runGenerate({ reason })}
      onSelect={(urn) => void runSelect(urn)}
      onRejectAll={() => setRejectOpen(true)}
    />
  );
  const hotelCard = (
    <HotelCard
      group={group}
      scope={scope}
      isSelected={isSelected}
      selectedSegments={selectedSegments}
      overnightAtHub={overnightAtHub}
      connectionUnvalidated={connectionUnvalidated}
      careOnly={careOnly}
    />
  );
  // The resolver's reason in words — an unrecognised one is not shown (never raw English).
  const policyWhy = policyReasonText(group.policyMatchReason, (k) => String(tRoster(k)));
  // `compact` (the replacement view): the reason rides on a hint over the link, not its own line.
  const policyNoteFor = (compact: boolean): ReactNode => {
    if (!group.governingPolicyUrn && !group.policyMatchType) return null;
    const why = policyWhy ? tRoster('drawer.policy.why', { reason: policyWhy }) : null;
    const scope = <span>{tRoster('drawer.policy.governedBy', { scope: policyScopeLabel(group.policyMatchType) })}</span>;
    const link = group.governingPolicyUrn ? (
      <Link to={`/policies/${encodeURIComponent(group.governingPolicyUrn)}`} className="text-primary hover:underline">
        {tRoster('drawer.policy.view')}
      </Link>
    ) : null;
    const hinted = compact && why;
    return (
      <div
        className="flex flex-col gap-0.5 px-1"
        data-testid="drawer-governing-policy"
        {...(group.governingPolicyUrn ? { title: group.governingPolicyUrn } : {})}
      >
        <p className="flex items-center gap-1.5 text-[13px] text-muted-foreground">
          <Scale className="h-3.5 w-3.5 shrink-0" aria-hidden />
          {hinted && !link ? <Hint body={why}>{scope}</Hint> : scope}
          {link ? hinted ? <Hint body={why}>{link}</Hint> : link : null}
        </p>
        {why && !compact ? (
          <p className="pl-5 text-[13px] text-muted-foreground/80" data-testid="drawer-governing-reason">
            {why}
          </p>
        ) : null}
      </div>
    );
  };
  const policyNote = policyNoteFor(false);

  // ── The trip in order.
  const arrivalIso = selectedSegments?.[selectedSegments.length - 1]?.arrival;
  const arrivalHours = arrivalIso ? (Date.parse(arrivalIso) - Date.now()) / 3_600_000 : undefined;
  const flightDone = isTicketed || !!externalBooking;
  const tripCells: TripCell[] = [
    {
      key: 'original',
      label: t('card.trip.original'),
      value: [group.flightNumber, group.departureAt ? clock.time(group.departureAt) : undefined].filter(Boolean).join(' · '),
      phase: 'cancelled',
      chipLabel: t('card.trip.disrupted'),
    },
    {
      key: 'wait',
      label: t('card.trip.wait', { station: group.origin }),
      value: mealsStep.value,
      phase: tripPhaseOf(mealsStep),
      view: 'meals',
      service: 'meals',
    },
    { key: 'hotel', label: t('card.trip.hotel'), value: hotelStep.value, phase: tripPhaseOf(hotelStep), view: 'hotel', service: 'hotel' },
    ...(flightStep
      ? [
          {
            key: 'flight' as const,
            label: t('card.trip.newFlight'),
            value: flightStep.value,
            phase: tripPhaseOf(flightStep),
            view: 'flight' as const,
            service: 'flight' as const,
          },
        ]
      : []),
    {
      key: 'arrival',
      label: t('card.trip.arrival'),
      value: [finalDest, arrivalIso ? clock.time(arrivalIso, finalDest) : undefined].filter(Boolean).join(' · '),
      ...(flightDone && arrivalHours !== undefined
        ? arrivalHours > 0
          ? { phase: 'inProgress' as const, chipLabel: t('card.trip.arrivesIn', { count: Math.max(1, Math.round(arrivalHours)) }) }
          : { phase: 'done' as const }
        : { phase: flightDone ? ('inProgress' as const) : ('pending' as const) }),
    },
  ];

  // ── The service cards.
  const flightActions: ReactNode[] = [];
  if (!purchase && legacyActionsAvailable && !resolved && !externalBooking && liveStatus !== 'NOT_APPLICABLE') {
    if (expired?.kind === 'options') {
      flightActions.push(
        <CardAction key="search-fresh" onClick={() => void runSearchFresh()} disabled={searchFreshPending} testId="pnr-flight-search-fresh">
          {searchFreshPending ? spinner : null}
          {t('band.action.searchFresh')}
        </CardAction>,
      );
    } else if (recommended) {
      flightActions.push(
        <CardAction key="select" onClick={() => void runSelect(recommended.urn)} disabled={busy} testId="pnr-flight-select">
          {busy ? spinner : null}
          {recommended.segments[0]?.flightNumber
            ? t('nextUp.select.primary', { flight: recommended.segments[0].flightNumber })
            : t('nextUp.select.primaryNoFlight')}
        </CardAction>,
      );
    } else if (liveStatus !== 'NO_OPTIONS' && policyGate?.enabled !== false && !careOnly) {
      flightActions.push(
        <CardAction key="generate" onClick={() => void runGenerate()} disabled={generate.isPending} testId="pnr-flight-generate">
          {generate.isPending ? spinner : null}
          {t('nextUp.generate.primary')}
        </CardAction>,
      );
    }
  }
  if (!purchase && legacyActionsAvailable && resolved && !isTicketed) {
    flightActions.push(
      <CardAction
        key="ticket"
        onClick={() => void runIssueTicket()}
        disabled={ticket.isPending || ticketBlockedByAcceptance}
        {...(ticketBlockedByAcceptance ? { title: t('nextUp.ticket.awaitingTitle') } : {})}
        testId="pnr-flight-ticket"
      >
        {ticket.isPending ? spinner : null}
        {t('nextUp.ticket.primary')}
      </CardAction>,
      <CardAction key="change" onClick={() => setCancelOpen(true)} testId="pnr-flight-change">
        {t('card.service.changeFlight')}
      </CardAction>,
    );
  }
  if (isTicketed) {
    flightActions.push(
      <CardAction key="voucher" onClick={() => utilities.print('flight-voucher')} testId="pnr-flight-voucher">
        {t('card.service.seeTicket')}
      </CardAction>,
    );
  }
  flightActions.push(
    <CardAction key="detail" onClick={() => setView('flight')} testId="pnr-flight-detail">
      {t('card.service.detail')}
    </CardAction>,
  );

  // A refusal that predates recovery, on a BFF without the recovery read: the plain lines.
  const showRecovery = !!purchase && hasRecovery(group) && !(purchase === 'REVIEW' && recoveryQuery.isError);
  const flightCard =
    flightStep ? (
      purchase === 'CONFIRMING' ? (
        <UnconfirmedPanel {...recoveryProps} />
      ) : (
        <ServiceCard
          title={t('card.service.flight')}
          service="flight"
          status={flightStep.state}
          statusTone={flightStep.tone}
          attention={tripPhaseOf(flightStep) === 'needsDecision'}
          testId="pnr-service-flight"
          actions={showRecovery ? undefined : flightActions}
        >
          {showRecovery && purchase ? (
            <RecoveryFlightBody {...recoveryProps} phase={purchase} onChoose={() => setView('replacement')} />
          ) : (
            <StepLines step={flightStep} />
          )}
          {externalBooking && !purchase ? (
            <p className="text-sm text-muted-foreground">
              {tLcc('receiptReference', { reference: externalBooking.bookingReference ?? '—' })}
            </p>
          ) : null}
        </ServiceCard>
      )
    ) : null;

  const mealsCard = (
    <ServiceCard
      title={t('card.service.meals')}
      service="meals"
      status={mealsStep.state}
      statusTone={mealsStep.tone}
      attention={tripPhaseOf(mealsStep) === 'needsDecision'}
      testId="pnr-service-meals"
      actions={
        <CardAction onClick={() => setView('meals')} testId="pnr-meals-manage">
          {cardFacts ? t('card.service.manageCard') : t('card.service.issueCard')}
        </CardAction>
      }
    >
      <StepLines step={mealsStep} />
    </ServiceCard>
  );

  const hotelActions: ReactNode[] = [];
  if (hotelNeeded && !reservation && routingUrn) {
    hotelActions.push(
      <CardAction
        key="allocate"
        onClick={() => allocate.mutate({ caseUrn: group.caseUrn, subCaseUrn: group.subCaseUrn, disruptionUrn: routingUrn })}
        disabled={allocate.isPending}
        testId="pnr-hotel-allocate"
      >
        {allocate.isPending ? spinner : null}
        {t('hotel.autoAllocate')}
      </CardAction>,
    );
  }
  hotelActions.push(
    <CardAction key="hotel" onClick={() => setView('hotel')} testId="pnr-hotel-open">
      {reservation ? t('card.service.seeHotel') : hotelNeeded ? t('band.action.chooseHotel') : t('card.service.assignAnyway')}
    </CardAction>,
    <CardAction key="transport" onClick={() => setView('transport')} testId="pnr-transport-open">
      {t('card.service.transport')}
    </CardAction>,
  );
  const hotelTransportCard = (
    <ServiceCard
      title={t('card.service.hotel')}
      service="hotel"
      status={hotelStep.state}
      statusTone={hotelStep.tone}
      attention={tripPhaseOf(hotelStep) === 'needsDecision' || tripPhaseOf(transportStep) === 'needsDecision'}
      testId="pnr-service-hotel"
      actions={hotelActions}
    >
      <StepLines step={hotelStep} />
      <div className="flex flex-wrap items-center gap-x-2 border-t border-border pt-2 text-sm">
        <span className="flex items-center gap-1.5 font-semibold">
          <ServiceIcon service="transfer" className="h-4 w-4" />
          {t('card.service.transferLabel')}
        </span>
        <span className={toneClasses(transportStep.tone).text} data-testid="pnr-service-transport-state">
          {transportStep.state}
        </span>
        <span className="text-muted-foreground">{transportStep.assigned.primary}</span>
      </div>
    </ServiceCard>
  );

  const banners = (
    <>
      {lockedByName ? (
        <div
          className="mx-4 mt-3 flex shrink-0 items-start gap-2 rounded-md border border-amber-400/50 bg-amber-50 p-2 text-sm text-amber-900 dark:border-amber-700/40 dark:bg-amber-900/20 dark:text-amber-100 sm:mx-6"
          role="status"
          aria-live="polite"
          data-testid="drawer-lock-conflict"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span>{tRoster('drawer.lockConflict', { pnr: group.pnrLabel, holder: lockedByName })}</span>
        </div>
      ) : null}
      {conflict ? (
        <div
          className="mx-4 mt-3 flex shrink-0 items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-2 text-sm text-destructive sm:mx-6"
          role="alert"
          data-testid="drawer-conflict"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span>
            <span className="font-semibold">{conflict.code}:</span> {conflict.message}
          </span>
        </div>
      ) : null}
      {actionError ? (
        <div
          className="mx-4 mt-3 flex shrink-0 items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-2 text-sm text-destructive sm:mx-6"
          role="alert"
          data-testid="drawer-action-error"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span>
            <span className="font-semibold">{actionError.title}:</span> {actionError.description}
          </span>
        </div>
      ) : null}
      {utilities.status ? <div className="mx-4 shrink-0 sm:mx-6">{utilities.status}</div> : null}
    </>
  );

  const back = (): void => setView('overview');
  const scrollBody = 'min-h-0 flex-1 overflow-y-auto p-4 sm:px-6';

  return (
    <>
      <PnrCardHeader
        group={group}
        position={position}
        onPrev={onPrev}
        onNext={onNext}
        moreActions={
          <>
          {/* «App del pasajero ▾» — copy their link / see the app as they do (2026-11-03). */}
          <PaxAppLink disruptionUrn={routingUrn} caseUrn={group.caseUrn} subCaseUrn={group.subCaseUrn} />
          <PnrMoreActions
            caseUrn={group.caseUrn}
            ticketed={isTicketed}
            onManualOverride={() => setOverrideMode('manual')}
            manualOverrideEnabled={legacyActionsAvailable}
            onExclude={() => setExcludeOpen(true)}
            utilities={utilities}
            onOpenActivity={() => setView('activity')}
            maximized={maximized}
            onToggleMaximize={onToggleMaximize}
          />
          </>
        }
      />
      {banners}

      {/* THE CARD — band + trip fixed; the services are the scroll region (§18.1). */}
      <CardViewPane value="overview" active={activeView}>
        <PnrStatusBand
          band={band}
          primary={band.primary ? bandButton(band.primary, 'primary') : null}
          secondary={band.secondary ? bandButton(band.secondary, 'secondary') : null}
        />
        <PnrTripStrip cells={tripCells} onOpen={setView} />
        <div className={cn(scrollBody, 'pt-3.5')} data-testid="pnr-content-scroll">
          <div className="grid items-start gap-3.5 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
            <div className="flex min-w-0 flex-col gap-3">
              {flightCard}
              {mealsCard}
              {hotelTransportCard}
            </div>
            <div className="flex min-w-0 flex-col gap-3">
              <PnrPaxSummary group={group} onOpen={() => setView('passenger')} />
              <PnrConversation
                group={group}
                notification={notification}
                clock={clock}
                answerFlight={answerFlight}
                purchaseFailed={purchaseFailed}
                onResend={() => utilities.runResend()}
                resendPending={utilities.resendPending}
                onCall={utilities.runCall}
                onContact={utilities.openContact}
                forceAccept={
                  // Always reachable while the offer awaits — unless the band already carries it.
                  awaiting && notification && band.secondary?.kind !== 'forceAccept' ? (
                    <ForceAcceptDialog
                      key={`force-${notification.offerHash ?? 'missing-offer'}`}
                      groupLabel={group.pnrLabel}
                      pending={force.isPending}
                      offerHashMissing={!notification.offerHash}
                      error={forceError}
                      onConfirm={doForce}
                    />
                  ) : null
                }
              />
              {policyNote}
            </div>
          </div>
        </div>
      </CardViewPane>

      {showFlight ? (
        <CardViewPane value="flight" active={activeView}>
          <ViewHeader title={t('card.service.flight')} onBack={back} />
          <div className={cn(scrollBody, 'space-y-4')}>
            {externalPurchaseOnly || externalBooking ? (
              <div className="overflow-hidden rounded-lg border border-border">
                <LccStatusBar caseUrn={group.caseUrn} groupId={group.subCaseUrn} />
              </div>
            ) : null}
            {manifestFlight ? (
              <section className="rounded-lg border border-border p-4" data-testid="pnr-manifest-next-flight">
                <FieldLabel>{t('manifestFlight.title')}</FieldLabel>
                <p className="mt-1.5 text-base font-semibold">
                  {[
                    manifestFlight.flightNumber,
                    manifestFlight.origin && manifestFlight.destination
                      ? `${manifestFlight.origin} → ${manifestFlight.destination}`
                      : undefined,
                    clock.when(manifestFlight.departureUtc, manifestFlight.origin ?? group.origin),
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
                <p className="mt-1 text-sm text-muted-foreground">{t('manifestFlight.hint')}</p>
              </section>
            ) : null}
            {resolved && legacyActionsAvailable ? (
              <div className="overflow-hidden rounded-lg border border-border">
                <ReaccommodationDiagram
                  group={group}
                  reservation={reservation}
                  selectedSegments={selectedSegments}
                  onward={selectedConnection}
                  overnightProvisional={provisionalOvernight}
                  overnightAtHub={overnightAtHub}
                  recovery={recovery}
                  isTicketed={isTicketed}
                  ticketNumber={ticketNumber}
                  holdExpiresAt={optionsQuery.data?.holdExpiresAt}
                  onIssueTicket={() => void runIssueTicket()}
                  ticketPending={ticket.isPending}
                  onChangeSelection={() => setCancelOpen(true)}
                  acceptanceState={notification?.acceptanceState}
                  departed={
                    expired?.kind === 'selected'
                      ? {
                          flight: expired.flight ?? '—',
                          when: expired.departAt ? clock.when(expired.departAt, group.origin) : '—',
                        }
                      : undefined
                  }
                  onSearchFresh={() => void runSearchFresh()}
                  searchPending={searchFreshPending}
                />
              </div>
            ) : (
              <JourneyRibbon
                legs={legs}
                finalDest={finalDest}
                isConnection={isConnection}
                disruptedIdx={disruptedIdx}
                connection={selectedConnection}
              />
            )}
            {resolved ? (
              <FlightDetails
                group={group}
                isTicketed={isTicketed}
                ticketNumber={ticketNumber}
                recovery={recovery}
                onward={selectedConnection}
                finalDestination={finalDest}
              />
            ) : null}
            {optionsPanel}
          </div>
        </CardViewPane>
      ) : null}

      {hasRecovery(group) ? (
        <CardViewPane value="replacement" active={activeView}>
          <ViewHeader title={t('card.view.replacement')} onBack={back} />
          <ReplacementChooser {...recoveryProps} policyLink={policyNoteFor(true)} />
        </CardViewPane>
      ) : null}

      <CardViewPane value="hotel" active={activeView}>
        <ViewHeader title={t('card.trip.hotel')} onBack={back} />
        <div className={scrollBody}>
          <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_20rem]">
            {goingHome ? (
              <GoingHomeCard home={goingHome} formatWhen={(iso) => clock.when(iso)} />
            ) : null}
            {hotelCard}
            <HotelWhyCard
              group={group}
              needs={hotelNeeded}
              reserved={!!reservation}
              sameDay={hotelSameDay}
              hubNight={!!group.overnightStation && group.overnightStation !== group.origin}
              hasCard={!!cardFacts || !!group.mealVoucher}
              transportArranged={transportArranged}
              transportOpen={destination !== null}
              hasFlight={hasAssigned}
              careOnly={careOnly}
              stayingHome={stayingHome}
            />
          </div>
        </div>
      </CardViewPane>

      <CardViewPane value="meals" active={activeView}>
        <ViewHeader title={t('card.service.meals')} onBack={back} />
        <div className={scrollBody}>
          <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_20rem]">
            <CompensationCardPanel group={group} scope={scope} />
            <EntitlementCard group={group} hotelInPlay={hotelNeeded || !!reservation} />
          </div>
        </div>
      </CardViewPane>

      <CardViewPane value="transport" active={activeView}>
        <ViewHeader title={t('card.service.transport')} onBack={back} />
        <div className={scrollBody}>
          <TransportCard
            group={group}
            scope={scope}
            transportPriceUsd={selectedOption?.priceBreakdown.transport}
            hasSelected={isSelected}
            destination={destination}
            hotelPending={!!reservation && reservation.status !== 'BOOKED'}
          />
        </div>
      </CardViewPane>

      <CardViewPane value="passenger" active={activeView} lazy>
        <ViewHeader title={t('card.pax.title', { count: 2 })} onBack={back} />
        <div className={cn(scrollBody, 'space-y-4')}>
          <PnrUtilityActions group={group} hasHotelReservation={!!reservation} variant="card" subset="all" />
          <div className="grid items-start gap-4 md:grid-cols-2">
            <OfferAcceptanceCard group={group} onCall={utilities.runCall} calling={utilities.resendPending} />
            <PassengersCard
              passengers={group.passengers}
              caseUrn={group.caseUrn}
              // The condition / responsible-adult writes (#806) are disruption-scoped.
              disruptionUrn={routingUrn ?? undefined}
              defaultOpen
            />
          </div>
        </div>
      </CardViewPane>

      <CardViewPane value="activity" active={activeView} lazy>
        <ViewHeader title={tActions('more.activity')} onBack={back} />
        <div className={scrollBody}>
          <ActivityPanel caseUrn={group.caseUrn} subCaseUrn={group.subCaseUrn} />
        </div>
      </CardViewPane>

      {utilities.overlays}
      <OverrideDialog
        scope={scope}
        group={group}
        mode={overrideMode}
        onClose={() => setOverrideMode(null)}
        onConflict={setConflict}
        onLockConflict={setLockedByName}
      />
      <CancelSelectionDialog
        scope={scope}
        group={group}
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        onLockConflict={setLockedByName}
      />
      <RejectOptionsDialog
        scope={scope}
        group={group}
        optionUrn={options[0]?.urn}
        open={rejectOpen}
        onClose={() => setRejectOpen(false)}
        onLockConflict={setLockedByName}
      />
      <ExcludeDialog scope={scope} group={group} open={excludeOpen} onClose={() => setExcludeOpen(false)} onDone={onClose} />
    </>
  );
}

/**
 * #811 — the passenger's own go-home decision, as the hotel view's first card: the
 * address (PII — this card is behind the operator login), whether a ride home was
 * requested and the advisory return pickup to the airport.
 */
function GoingHomeCard({ home, formatWhen }: { home: IGoingHome; formatWhen: (iso: string) => string }) {
  const { t } = useTranslation('pnr');
  return (
    <section
      className="rounded-lg border border-border bg-card p-4 text-sm"
      data-testid="drawer-going-home"
      aria-label={t('home.title')}
    >
      <div className="flex items-center gap-2 font-medium">
        <Home className="h-4 w-4" aria-hidden />
        {t('home.title')}
      </div>
      <p className="mt-1 text-muted-foreground">{t('home.byPassenger')}</p>
      <dl className="mt-3 grid gap-1">
        <div className="flex gap-2">
          <dt className="text-muted-foreground">{t('home.address')}:</dt>
          <dd>{home.address ?? t('home.addressUnknown')}</dd>
        </div>
        <div className="flex gap-2">
          <dd>{home.wantsTransportHome ? t('home.transportRequested') : t('home.transportNotRequested')}</dd>
        </div>
        {home.wantsTransportHome ? (
          <div className="flex gap-2">
            {home.returnPickupAt ? (
              <>
                <dt className="text-muted-foreground">{t('home.returnPickup')}:</dt>
                <dd>{formatWhen(home.returnPickupAt)}</dd>
              </>
            ) : (
              <dd>{t('home.returnPickupPending')}</dd>
            )}
          </div>
        ) : null}
        {home.decidedAt ? (
          <div className="text-xs text-muted-foreground">{t('home.decidedAt', { when: formatWhen(home.decidedAt) })}</div>
        ) : null}
      </dl>
    </section>
  );
}
